import { useState, useMemo, useEffect, useCallback } from "react";
import { StateManager } from "../service/StateManager.js";
import { X, Package, MapPin, CheckCircle, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import Portal from "./Portal.jsx";
import "../../../css/OnRoad.css";

export default function RemesaSelectionModal({ 
    isOpen, 
    onClose, 
    onConfirm,
    operacionData,
    fase,
    evento
}) {
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedRemesas, setSelectedRemesas] = useState([]);

    const obtenerCampoFecha = useCallback((fase, evento) => {
        const mapeoFechas = {
            'CARGUE': {
                'LLEGADA': 'fecha_llegada_cargue',
                'SALIDA': 'fecha_salida_cargue'
            },
            'DESCARGUE': {
                'LLEGADA': 'fecha_llegada_descargue',
                'SALIDA': 'fecha_salida_descargue'
            }
        };

        return mapeoFechas[fase]?.[evento] || null;
    }, []);

    const remesaTieneEventoReportado = useCallback((remesa, fase, evento) => {
        const campoFecha = obtenerCampoFecha(fase, evento);
        
        if (!campoFecha) {
            console.warn(`Campo de fecha no encontrado para ${fase} - ${evento}`);
            return false;
        }

        // Si el campo de fecha tiene valor (no es null), ya está reportado
        return remesa[campoFecha] !== null && remesa[campoFecha] !== undefined;
    }, [obtenerCampoFecha]);

    const remesaCumplePrerequisitos = useCallback((remesa, fase, evento) => {
        // Para SALIDA, verificar que LLEGADA esté reportado
        if (evento === 'SALIDA') {
            const campoLlegada = obtenerCampoFecha(fase, 'LLEGADA');
            if (!remesa[campoLlegada]) {
                return false; // No puede reportar SALIDA sin LLEGADA
            }
        }

        return true;
    }, [obtenerCampoFecha]);

    const estadoDetallado = useMemo(() => {
        if (!operacionData?.remesas || !fase || !evento) {
            return null;
        }

        return StateManager.obtenerEstadoDetalladoPorEvento(
            fase,
            evento,
            operacionData
        );
    }, [operacionData, fase, evento]);

    const gruposPendientesIds = useMemo(() => {
        if (!estadoDetallado?.gruposPendientes) {
            return new Set();
        }

        return new Set(
            estadoDetallado.gruposPendientes.map(g => g.id)
        );
    }, [estadoDetallado]);

    const gruposRemesas = useMemo(() => {
        if (!operacionData?.remesas || operacionData.remesas.length === 0) {
            return [];
        }

        const remesas = operacionData.remesas;
        const grupos = {};

        remesas.forEach(remesa => {
            // VALIDACIÓN: Solo procesar remesas que NO tengan el evento reportado
            // y que cumplan con los prerequisitos
            if (remesaTieneEventoReportado(remesa, fase, evento)) {
                return; // Saltar esta remesa, ya tiene el evento reportado
            }

            if (!remesaCumplePrerequisitos(remesa, fase, evento)) {
                return; // Saltar esta remesa, no cumple prerequisitos
            }

            // Determinar la clave de agrupación según la fase
            let claveGrupo, ciudadGrupo, daneGrupo;
            
            if (fase === 'CARGUE') {
                claveGrupo = remesa.origen_dane;
                ciudadGrupo = remesa.origen;
                daneGrupo = remesa.origen_dane;
            } else { // DESCARGUE
                claveGrupo = remesa.destino_dane;
                ciudadGrupo = remesa.destino;
                daneGrupo = remesa.destino_dane;
            }

            const idCompleto = `${remesa.origen_dane}_${remesa.destino_dane}`;

            // Crear grupo si no existe
            if (!grupos[claveGrupo]) {
                grupos[claveGrupo] = {
                    id: claveGrupo,
                    idCompleto,
                    ciudad: ciudadGrupo,
                    codigo_dane: daneGrupo,
                    remesas: []
                };
            }

            // Agregar remesa al grupo
            grupos[claveGrupo].remesas.push({
                id: remesa.id,
                id_remesa: remesa.id_remesa,
                consecutivo: remesa.consecutivo,
                producto: remesa.producto,
                propietario: remesa.propietario,
                nombre_propietario: remesa.nombre_propietario,
                origen: remesa.origen,
                destino: remesa.destino,
                origen_dane: remesa.origen_dane,
                destino_dane: remesa.destino_dane
            });
        });

        const gruposArray = Object.values(grupos);
        
        // Filtrar grupos que tienen al menos una remesa válida
        // Y que coinciden con los grupos pendientes del StateManager
        const gruposFiltrados = gruposArray.filter(grupo => 
            grupo.remesas.length > 0 && 
            gruposPendientesIds.has(grupo.idCompleto)
        );

        return gruposFiltrados;
    }, [operacionData, fase, evento, gruposPendientesIds, remesaTieneEventoReportado, remesaCumplePrerequisitos]);

    // Auto-selección si solo hay 1 grupo con 1 remesa pendiente
    useEffect(() => {
        if (!isOpen || !gruposRemesas.length) return;

        // Si solo hay un grupo con una remesa, auto-confirmar
        if (gruposRemesas.length === 1 && gruposRemesas[0].remesas.length === 1) {
            const grupo = gruposRemesas[0];
            const datosSeleccion = {
                grupoId: grupo.id,
                ciudad: grupo.ciudad,
                codigo_dane: grupo.codigo_dane,
                remesas: grupo.remesas,
                remesaIds: grupo.remesas.map(r => r.id),
                fase,
                origen_dane: grupo.remesas[0].origen_dane,
                destino_dane: grupo.remesas[0].destino_dane
            };

            // Se puede eliminar en caso de que sea manual
            onConfirm(datosSeleccion);
            onClose();
        }
    }, [isOpen, gruposRemesas, onConfirm, onClose, fase]);

    // Maneja la selección de un grupo completo de remesas
    const handleSelectGroup = (grupo) => {
        const todasLasRemesasIds = grupo.remesas.map(r => r.id);
        const grupoEstaCompleto = todasLasRemesasIds.every(id => selectedRemesas.includes(id));
        
        if (grupoEstaCompleto) {
            // Deseleccionar todo el grupo
            setSelectedRemesas(prev => 
                prev.filter(id => !todasLasRemesasIds.includes(id))
            );
            // Si se deseleccionan todas, limpiar el grupo seleccionado
            setSelectedGroup(null);
        } else {
            // Seleccionar todo el grupo
            setSelectedGroup(grupo.id);
            setSelectedRemesas(todasLasRemesasIds);
        }
    };

    // Maneja la selección individual de una remesa
    const handleToggleRemesa = (remesaId, grupoId) => {
        setSelectedGroup(grupoId);
        
        setSelectedRemesas(prev => {
            if (prev.includes(remesaId)) {
                // Deseleccionar
                const nuevasSeleccionadas = prev.filter(id => id !== remesaId);
                
                // Si no quedan remesas seleccionadas, limpiar el grupo
                if (nuevasSeleccionadas.length === 0) {
                    setSelectedGroup(null);
                }
                
                return nuevasSeleccionadas;
            } else {
                // Seleccionar - solo permitir del mismo grupo
                const grupo = gruposRemesas.find(g => g.id === grupoId);
                const remesasDelGrupo = grupo.remesas.map(r => r.id);
                
                // Filtrar solo las remesas del grupo actual
                const remesasDelGrupoSeleccionadas = prev.filter(id => 
                    remesasDelGrupo.includes(id)
                );
                
                return [...remesasDelGrupoSeleccionadas, remesaId];
            }
        });
    };

    // Confirma la selección y cierra el modal
    const handleConfirm = () => {
        if (!selectedGroup || selectedRemesas.length === 0) {
            toast.warning('Debe seleccionar al menos una remesa');
            return;
        }

        // Obtener el grupo seleccionado
        const grupo = gruposRemesas.find(g => g.id === selectedGroup);

        if (!grupo) {
            toast.error('No se encontró el grupo seleccionado');
            console.error('Grupo no encontrado:', selectedGroup);
            return;
        }
        
        // Obtener las remesas completas seleccionadas
        const remesasCompletas = grupo.remesas.filter(r => 
            selectedRemesas.includes(r.id)
        );

        // Preparar datos para enviar
        const datosSeleccion = {
            grupoId: selectedGroup,
            ciudad: grupo.ciudad,
            codigo_dane: grupo.codigo_dane,
            remesas: remesasCompletas,
            remesaIds: selectedRemesas,
            fase,
            origen_dane: remesasCompletas[0].origen_dane,
            destino_dane: remesasCompletas[0].destino_dane
        };

        onConfirm(datosSeleccion);
        handleClose();
    };

    // Cierra el modal y limpia la selección
    const handleClose = () => {
        setSelectedGroup(null);
        setSelectedRemesas([]);
        onClose();
    };

    // Verifica si todas las remesas de un grupo están seleccionadas
    const isGrupoCompleto = (grupo) => {
        return grupo.remesas.every(r => selectedRemesas.includes(r.id));
    };

    // Verifica si alguna remesa del grupo está seleccionada
    const isGrupoParcial = (grupo) => {
        return grupo.remesas.some(r => selectedRemesas.includes(r.id)) && 
               !isGrupoCompleto(grupo);
    };

    if (!isOpen) return null;

    const faseLabel = fase === 'CARGUE' ? 'CARGUE' : 'DESCARGUE';
    const ubicacionLabel = fase === 'CARGUE' ? 'Origen' : 'Destino';

    return (
        <Portal>
            <div className="sheet-backdrop remesa-selection-backdrop">
                <div className="sheet-container remesa-selection-modal">
                    {/* Header */}
                    <div className="sheet-header">
                        <div className="sheet-title">
                            <Package size={20} />
                            Seleccionar Remesas
                        </div>
                        <button
                            className="sheet-close-btn"
                            onClick={handleClose}
                            aria-label="Cerrar"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    
                    {/* Content */}
                    <div className="sheet-content remesa-selection-content">
                        {/* Información del contexto */}
                        <div className="selection-context-info">
                            <div className="context-badge">
                                <MapPin size={16} />
                                <span>Fase: {faseLabel}</span>
                            </div>
                            <div className="context-badge">
                                <Package size={16} />
                                <span>Evento: {evento}</span>
                            </div>
                        </div>

                        {/* Instrucciones */}
                        <div className="selection-instructions">
                            <p>Seleccione las remesas con el mismo <strong>{ubicacionLabel}</strong> a reportar:</p>
                        </div>

                        {/* Lista de grupos de remesas */}
                        <div className="grupos-remesas-list">
                            {gruposRemesas.map((grupo) => (
                                <div 
                                    key={grupo.id} 
                                    className={`grupo-remesas-card ${
                                        selectedGroup === grupo.id ? 'selected' : ''
                                    } ${isGrupoParcial(grupo) ? 'partial' : ''}`}
                                >
                                    {/* Header del grupo */}
                                    <div 
                                        className="grupo-header"
                                        onClick={() => handleSelectGroup(grupo)}
                                    >
                                        <div className="grupo-info">
                                            <div className="grupo-ciudad">
                                                <MapPin size={16} />
                                                <span>{grupo.ciudad}</span>
                                            </div>
                                        </div>
                                        <div className="grupo-checkbox-container">
                                            <div 
                                                className={`grupo-checkbox ${
                                                    isGrupoCompleto(grupo) ? 'checked' : ''
                                                } ${isGrupoParcial(grupo) ? 'partial' : ''}`}
                                            >
                                                {isGrupoCompleto(grupo) && <CheckCircle size={16} />}
                                                {isGrupoParcial(grupo) && (
                                                    <span className="partial-indicator">━</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Lista de remesas del grupo */}
                                    {selectedGroup === grupo.id && (
                                        <div className="remesas-list-expanded">
                                            {grupo.remesas.map((remesa) => (
                                                <div
                                                    key={remesa.id}
                                                    className={`remesa-item ${
                                                        selectedRemesas.includes(remesa.id) ? 'selected' : ''
                                                    }`}
                                                    onClick={() => handleToggleRemesa(remesa.id, grupo.id)}
                                                >
                                                    <div className="remesa-checkbox">
                                                        <div className={`checkbox ${
                                                            selectedRemesas.includes(remesa.id) ? 'checked' : ''
                                                        }`}>
                                                            {selectedRemesas.includes(remesa.id) && (
                                                                <CheckCircle size={14} />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="remesa-details">
                                                        <div className="remesa-header-info">
                                                            <span className="remesa-consecutivo">
                                                                {remesa.consecutivo || `Remesa #${remesa.id_remesa}`}
                                                            </span>
                                                            <span className="remesa-producto">
                                                                {remesa.producto}
                                                            </span>
                                                        </div>
                                                        <div className="remesa-ruta">
                                                            <span className="ruta-text">
                                                                {remesa.origen} → {remesa.destino}
                                                            </span>
                                                        </div>
                                                        <div className="remesa-cliente">
                                                            <span className="cliente-text">
                                                                {remesa.nombre_propietario || remesa.propietario}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Resumen de selección */}
                        {selectedRemesas.length > 0 && (
                            <div className="selection-summary">
                                <CheckCircle size={16} className="text-success" />
                                <span>
                                    {selectedRemesas.length} remesa{selectedRemesas.length !== 1 ? 's' : ''} seleccionada{selectedRemesas.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="sheet-footer">
                        <button
                            className="btn-cancel"
                            onClick={handleClose}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn-confirm-selection"
                            onClick={handleConfirm}
                            disabled={selectedRemesas.length === 0}
                        >
                            <ChevronRight size={18} />
                            Continuar con {selectedRemesas.length} remesa{selectedRemesas.length !== 1 ? 's' : ''}
                        </button>
                    </div>
                </div>
            </div>
        </Portal>
    );
}